/**
 * Audit log viewer (CLAUDE.md 12.2, 15.10).
 *
 * The action filter matches on a **prefix**, because that is how the log is actually
 * read: "show me everything an administrator did" is `admin.`, "show me the
 * authentication events" is `auth.`. Requiring an exact verb would mean knowing the
 * vocabulary before you could look anything up.
 *
 * Timestamps are ISO-8601 in UTC rather than locale-formatted. An audit trail is read
 * for ordering and for correlation with server logs, and `02/03` meaning two different
 * days in the two launch languages is exactly the ambiguity to avoid here.
 */
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { PAGE_SIZE, useAuditLog } from '@/api/admin'
import { Pagination, TableScroller, Td, Th, formatDateTime } from '@/components/admin/AdminShell'
import { Input } from '@/components/ui/input'

const PREFIXES = ['', 'admin.', 'auth.', 'project.', 'position.', 'workspace.', 'share_link.']

export function AdminAudit() {
  const { t } = useTranslation(['admin', 'common'])

  const [action, setAction] = useState('')
  const [userId, setUserId] = useState('')
  const [offset, setOffset] = useState(0)

  const parsedUserId = Number(userId)
  const audit = useAuditLog({
    action: action || undefined,
    user_id: userId && Number.isFinite(parsedUserId) ? parsedUserId : undefined,
    offset,
  })

  return (
    <div className="space-y-4">
      <h2 className="text-lg font-semibold">{t('admin.audit.title')}</h2>

      <div className="flex flex-wrap gap-2">
        <select
          className="h-11 rounded-md border border-border bg-surface px-3 text-text"
          aria-label={t('admin.audit.filter_action')}
          value={PREFIXES.includes(action) ? action : ''}
          onChange={(event) => {
            setAction(event.target.value)
            setOffset(0)
          }}
        >
          {PREFIXES.map((prefix) => (
            <option key={prefix || 'all'} value={prefix}>
              {prefix || t('common.all', { ns: 'common' })}
            </option>
          ))}
        </select>
        <Input
          className="max-w-40"
          inputMode="numeric"
          placeholder={t('admin.audit.filter_user')}
          value={userId}
          onChange={(event) => {
            setUserId(event.target.value.replace(/\D/g, ''))
            setOffset(0)
          }}
        />
      </div>

      {audit.data && audit.data.items.length === 0 ? (
        <p className="rounded-lg border border-border p-6 text-center text-sm text-text-muted">
          {t('admin.audit.empty')}
        </p>
      ) : (
        <>
          <TableScroller>
            <thead>
              <tr>
                <Th>{t('admin.audit.when')}</Th>
                <Th>{t('admin.audit.who')}</Th>
                <Th>{t('admin.audit.action')}</Th>
                <Th>{t('admin.audit.entity')}</Th>
                <Th>{t('admin.audit.meta')}</Th>
              </tr>
            </thead>
            <tbody>
              {audit.data?.items.map((row) => (
                <tr key={row.id}>
                  <Td className="whitespace-nowrap font-mono text-xs text-text-muted">
                    {formatDateTime(row.created_at)}
                  </Td>
                  <Td>
                    <div>{row.username ?? '—'}</div>
                    {row.ip && <div className="font-mono text-xs text-text-muted">{row.ip}</div>}
                  </Td>
                  {/* Action names are identifiers, not prose: never translated. */}
                  <Td className="font-mono text-xs">{row.action}</Td>
                  <Td className="font-mono text-xs text-text-muted">
                    {row.entity ? `${row.entity}#${row.entity_id ?? '—'}` : '—'}
                  </Td>
                  <Td className="max-w-sm">
                    {row.meta ? (
                      <pre className="overflow-x-auto text-xs text-text-muted">
                        {JSON.stringify(row.meta)}
                      </pre>
                    ) : (
                      '—'
                    )}
                  </Td>
                </tr>
              ))}
            </tbody>
          </TableScroller>

          <Pagination
            total={audit.data?.total ?? 0}
            limit={PAGE_SIZE}
            offset={offset}
            onChange={setOffset}
          />
        </>
      )}
    </div>
  )
}
